// The conversation thread, styled to the mocks: user turns as right-aligned
// accent bubbles, assistant turns as the "Thinking…" status (while live) above
// the prose answer. Auto-scrolls to the newest content as events stream in.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { reconcileRenderSurfaces } from "@safetyqube/sq-ai-react";
import styles from "../sqAi.module.css";
import ChartCard from "./chart/ChartCard";
import ThinkingStatus from "./ThinkingStatus";
import ExportPanel from "./ExportPanel";
import MessageTime from "./MessageTime";
import { splitFollowups } from "../followupBlock.mjs";

// How close to the bottom still counts as "following along".
const STICK_THRESHOLD_PX = 64;

// Dev token/cost readout for one turn.
//
// It renders exactly what the server chose to send and nothing else: each section
// exists only when the matching SQ_AI_SHOW_* switch is on server-side, so an absent
// section is absent from the payload, not merely hidden here. There is no flag for
// this component to read and no default that could turn a section on — with every
// switch off (the default) `costDisplay` is undefined and this renders nothing.
//
// Fresh and cached are shown SEPARATELY rather than as one input figure because they
// bill an order of magnitude apart; a single "input" number here would misrepresent
// the cost of every turn.
function CostMeta({ costDisplay }) {
  if (!costDisplay) return null;
  const { tokens, messageUsd, chatUsd } = costDisplay;
  const n = (v) => v.toLocaleString();
  // 4 decimal places: a turn costs on the order of a cent, so 2 would round most
  // turns to $0.01 or $0.00 and hide the differences this readout exists to show.
  const usd = (v) => (v === null || v === undefined ? "unpriced" : `$${v.toFixed(4)}`);
  return (
    <div className={styles.costMeta}>
      {tokens && (
        <span>
          {n(tokens.freshInput)} fresh · {n(tokens.cachedInput)} cached · {n(tokens.output)} out
          {" · "}
          {tokens.calls.length} {tokens.calls.length === 1 ? "call" : "calls"}
        </span>
      )}
      {messageUsd !== undefined && <span className={styles.costMetaCost}>{usd(messageUsd)} this message</span>}
      {chatUsd !== undefined && <span className={styles.costMetaCost}>{usd(chatUsd)} this chat</span>}
    </div>
  );
}

export default function Thread({ messages, onSuggestionClick, suggestionsDisabled, env, tenantSchema, scrollRef, times, userTz }) {
  // Follow the stream ONLY while the reader is already at the bottom, and let go
  // the moment they scroll away — otherwise every streamed token yanks the view
  // back down and it's impossible to read back through a long answer.
  const stick = useRef(true);
  const [detached, setDetached] = useState(false);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef && scrollRef.current;
    if (!el) return;
    // Instant, not smooth: a smooth scroll is still travelling when the next token
    // arrives, so the listener below would sample a midway position and detach.
    el.scrollTop = el.scrollHeight;
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef && scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      const atBottom = distance <= STICK_THRESHOLD_PX;
      stick.current = atBottom;
      setDetached(!atBottom);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef]);

  // A new turn re-attaches — the reader just asked something and wants to watch.
  const turnCount = messages.length;
  useEffect(() => {
    stick.current = true;
    setDetached(false);
    scrollToBottom();
  }, [turnCount, scrollToBottom]);

  // Follow the stream itself, keyed on the last caption (the message COUNT changes
  // only once per turn, which would leave a long reply growing off the bottom).
  const lastCaption = messages.length ? messages[messages.length - 1].caption : "";
  useLayoutEffect(() => {
    if (stick.current) scrollToBottom();
  }, [lastCaption, scrollToBottom]);

  const jumpToLatest = () => {
    stick.current = true;
    setDetached(false);
    scrollToBottom();
  };

  return (
    <div className={styles.thread}>
      {messages.map((msg, i) => (
        <div className={styles.row} key={msg.id}>
          {msg.role === "user" ? (
            <UserBubble message={msg} at={times ? times[msg.id] : null} zone={userTz} />
          ) : (
            <AssistantTurn
              message={msg}
              isLast={i === messages.length - 1}
              onSuggestionClick={onSuggestionClick}
              suggestionsDisabled={suggestionsDisabled}
              env={env}
              tenantSchema={tenantSchema}
              at={times ? times[msg.id] : null}
              zone={userTz}
            />
          )}
        </div>
      ))}

      {detached && (
        <div className={styles.jumpWrap}>
          <button type="button" className={styles.jumpBtn} onClick={jumpToLatest}>
            ↓ Jump to latest
          </button>
        </div>
      )}
    </div>
  );
}

function UserBubble({ message, at, zone }) {
  return (
    <div className={styles.userRow}>
      <div className={styles.userTurn}>
        <div className={styles.userBubble}>{message.text}</div>
        <MessageTime at={at} zone={zone} side="user" />
      </div>
    </div>
  );
}

function AssistantTurn({ message, isLast, onSuggestionClick, suggestionsDisabled, env, tenantSchema, at, zone }) {
  const caption = (message.caption || "").trim();
  // ONE table per turn (Part 5): a CHART turn shows the chart + text (the export
  // table + CSV would duplicate it → suppressed); a LIST turn shows the structured
  // export table + CSV (the LLM emits a one-line summary, no markdown table). See
  // reconcileRenderSurfaces for the intent rule.
  const { exportDescriptor, showExportPanel, chartsToRender } = reconcileRenderSurfaces(message);
  const hasChart = chartsToRender.length > 0;
  const finishedEmpty = message.done && !message.error && !hasChart && !caption && !showExportPanel;
  const thinking = !message.done;
  const suggestions = message.suggestions || [];
  // NOTHING UNDER THE ANSWER. The "Counted by" tag and the SPEC_06 coverage sentence were drawn here
  // from `resultMeta`; product asked for no chip under an answer, and the engine stopped sending both
  // (its wire copy carries `dateBasisLabel: null` and no basis objects — the basis is in the answer's
  // own words now). A turn from before that still carries the fields, so they are not read at all
  // rather than trusted to be empty. The table panel is not a chip: its row count and its download
  // stay with ExportPanel.
  //
  // THE FOLLOW-UPS ARE DRAWN LAST. They are the caption's tail and the table is drawn after the caption,
  // so a turn with a table cuts the caption at the follow-up heading and draws the block under the table
  // (see followupBlock). A chart is already drawn above the caption; a turn with no table renders whole.
  const { body, followups } = showExportPanel ? splitFollowups(caption) : { body: caption, followups: "" };

  return (
    <div>
      <ThinkingStatus steps={message.thinkingSteps || []} thinking={thinking} thinkingTick={message.thinkingTick || 0} />

      {message.assumptions.map((a, i) => (
        <div className={styles.assumption} key={i}>
          <span aria-hidden="true">ⓘ</span>
          <span>
            {a.text}
            {a.rationale && <span className={styles.assumptionRationale}> — {a.rationale}</span>}
          </span>
        </div>
      ))}

      {chartsToRender.map((spec, i) => (
        <ChartCard
          key={i}
          spec={spec}
          exportMeta={exportDescriptor ? exportDescriptor.exportMeta : null}
          done={!!message.done}
        />
      ))}

      {body && (
        <div className={`${styles.prose} ${styles.assistantCaption}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </div>
      )}

      {showExportPanel && (
        <ExportPanel descriptor={exportDescriptor} env={env} tenantSchema={tenantSchema} zone={zone} />
      )}

      {followups && (
        <div className={`${styles.prose} ${styles.assistantCaption}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{followups}</ReactMarkdown>
        </div>
      )}

      {message.error && (
        <div className={styles.errorBox}>
          <span aria-hidden="true">⚠</span>
          <div>
            <div className={styles.errorMessage}>{message.error.message || "Something went wrong."}</div>
            {message.error.code && <div className={styles.assumptionRationale}>{message.error.code}</div>}
          </div>
        </div>
      )}

      {finishedEmpty && (
        <p className={styles.prose}>I couldn't put together a result for that one. Could you ask it a different way?</p>
      )}

      <CostMeta costDisplay={message.costDisplay} />

      {/* The answer's time: under the answer and above its buttons — it belongs to the message, not to
          the follow-ups. Stamped when the turn FINISHED, so nothing shows while it streams. */}
      <MessageTime at={at} zone={zone} side="answer" />

      {isLast && suggestions.length > 0 && (
        <div className={styles.suggestRow}>
          {suggestions.map((s, i) => (
            <button
              key={s.query || s.label || i}
              type="button"
              className={styles.example}
              onClick={() => onSuggestionClick && onSuggestionClick(s.query || s.label)}
              disabled={suggestionsDisabled}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <SqlAccordion sqls={message.sqls} />
    </div>
  );
}

// Dev-only: the SQL only reaches the UI in uiMode "dev" / when devSql is set.
function SqlAccordion({ sqls }) {
  if (!sqls || sqls.length === 0) return null;
  return (
    <details className={styles.sqlDetails}>
      <summary className={styles.sqlToggle}>Show generated SQL{sqls.length > 1 ? ` (${sqls.length})` : ""}</summary>
      <div>
        {sqls.map((s, i) => (
          <div key={s.id || i}>
            <div className={styles.sqlName}>{s.name || `query ${i + 1}`}</div>
            <pre className={styles.sqlPre}>{s.sql}</pre>
          </div>
        ))}
      </div>
    </details>
  );
}
